{
  "common": {
    "skipToContent": "Skip to main content",
    "footer": {
      "copyright": "PAC Hub © 2024–{{year}}. All rights reserved.",
      "followX": "Follow us on X",
      "followLinkedIn": "Follow us on LinkedIn"
    }
  },
  "marketing": {
    "nav": {
      "home": "Home",
      "features": "Features",
      "about": "About",
      "howItWorks": "How it works",
      "guides": "Guides",
      "faq": "FAQ",
      "contact": "Contact",
      "menuToggle": "Open navigation menu"
    },
    "languageSwitcher": {
      "label": "Language",
      "en": "English",
      "fr": "Français",
      "zh": "中文 (简体)"
    },
    "meta": {
      "home": {
        "title": "PAC Hub | One login. One parent community.",
        "description": "One login. One parent community. PAC Hub is the all-in-one, secure platform for Parent Advisory Councils."
      },
      "features": {
        "title": "Features | PAC Hub",
        "description": "One consolidated platform for Parent Advisory Councils: calendar, volunteers, lunch, and more on your school site with a single parent login."
      },
      "howItWorks": {
        "title": "How it works | PAC Hub",
        "description": "Quick setup for your Parent Advisory Council at yourschool.pachub.ca: school site, parent registration, and roles that match how councils run."
      },
      "guides": {
        "title": "Guides | PAC Hub",
        "description": "Short walkthroughs for signing in and using PAC Hub."
      },
      "guide": {
        "title": "{{title}} | PAC Hub",
        "description": "A short PAC Hub walkthrough. {{title}}."
      },
      "about": {
        "title": "About PAC Hub",
        "description": "Built by PACs for PACs. Canadian-owned software for Parent Advisory Councils."
      },
      "faq": {
        "title": "FAQ | PAC Hub",
        "description": "Answers to common questions about PAC Hub for Parent Advisory Councils."
      },
      "contact": {
        "title": "Contact PAC Hub",
        "description": "Contact PAC Hub about pilot schools and one consolidated platform for your Parent Advisory Council."
      },
      "privacy": {
        "title": "Privacy Policy | PAC Hub",
        "description": "How PAC Hub collects, uses, and protects information submitted through our marketing website."
      },
      "notFound": {
        "title": "Page not found | PAC Hub"
      }
    },
    "jsonLd": {
      "organizationDescription": "One login. One parent community. PAC Hub is the all-in-one, secure platform for Parent Advisory Councils."
    },
    "notFound": {
      "title": "Page not found",
      "description": "That page is not available on the PAC Hub marketing site.",
      "returnHome": "Return home"
    },
    "footer": {
      "privacyPolicy": "Privacy Policy",
      "about": "About",
      "guides": "Guides",
      "faq": "FAQ"
    },
    "hero": {
      "title": "One login.\nOne parent community.",
      "tagline": "The all-in-one platform for Parent Advisory Councils. Safe, secure, and built for your school community.",
      "primaryCta": "See what's included",
      "secondaryCta": "Join a pilot school"
    },
    "home": {
      "pillarsTitle": "Why PAC Hub works",
      "pillarsDescription": "One consolidated platform for Parent Advisory Councils. A single place for your school community to access all your tools.",
      "pillarAllInOneTitle": "All-in-one for parents",
      "pillarAllInOneDescription": "Parents sign in once at your school's PAC Hub site instead of creating a separate account for every tool your council uses.",
      "pillarOnePlaceTitle": "Every PAC tool, one site",
      "pillarOnePlaceDescription": "Events, volunteer sign-ups, hot lunch, messages, and forms on the same school site every time, so parents spend less time hunting for links.",
      "pillarTrustTitle": "Safe, secure, yours",
      "pillarTrustDescription": "Modern authentication and school-scoped data. We do not sell your community's data to third parties.",
      "howItWorksTeaserTitle": "How it works",
      "howItWorksTeaserDescription": "Your council gets one school site. Families register once. Everyone runs the year from the same place.",
      "howItWorksTeaserCta": "See how it works",
      "steps": {
        "schoolSite": {
          "title": "Your school gets a site",
          "description": "Your PAC lives at yourschool.pachub.ca, your new home for your parent community."
        },
        "register": {
          "title": "Families register once",
          "description": "Parents sign up with a school code, verify email, and link children to classrooms."
        },
        "runYear": {
          "title": "All your PAC tools",
          "description": "Hot lunch, events, messages, and meetings from one login."
        }
      },
      "includedTitle": "What's included",
      "includedDescription": "Everything your PAC needs in one place.",
      "includedItems": {
        "hotLunch": "Hot Lunch",
        "volunteers": "Volunteers",
        "budget": "Budget",
        "communications": "Communications",
        "meetings": "Meetings",
        "fundraising": "Fundraising"
      },
      "includedLearnMore": "Learn more",
      "trustTitle": "Trust by design",
      "pilotCtaTitle": "Ready to simplify your PAC?",
      "pilotCtaDescription": "We're onboarding pilot schools. Tell us about your council and we'll share how PAC Hub can work for your community.",
      "pilotCtaButton": "Get in touch"
    },
    "featuresPage": {
      "metaTitle": "Features | PAC Hub",
      "metaDescription": "One consolidated platform for Parent Advisory Councils: calendar, volunteers, lunch, and more on your school site with a single parent login.",
      "heroTitle": "Everything your PAC needs",
      "heroDescription": "One school site. One parent login. Tools built for Parent Advisory Councils.",
      "pilotCtaTitle": "Ready to simplify your PAC?",
      "pilotCtaDescription": "We're onboarding pilot schools. Tell us about your council and we'll share how PAC Hub can work for your community.",
      "pilotCtaButton": "Get in touch"
    },
    "featureDomains": {
      "hotLunch": {
        "title": "Hot lunch",
        "pitch": "Purpose-built lunch ordering on your school site, included in your subscription.",
        "bullets": [
          "Order for every child on one screen. Multi-date cart and one checkout.",
          "Per-day cutoffs, allergy notes at order time, and family profiles linked to classrooms",
          "Vendor work orders, class distribution lists, and label exports for coordinators",
          "PAC extras for discreet meal coverage without charity labels on class lists",
          "Secure payment processing on your school site. Lunch is core to the hub, not an add-on."
        ]
      },
      "calendar": {
        "title": "Calendar & events",
        "pitch": "Publish your PAC community calendar and schedule activities on your school site.",
        "bullets": [
          "Create and publish in-person, virtual, and hybrid PAC activities",
          "Optional calendar visibility and home dashboard widgets for parents",
          "Attach documents from your PAC library to events",
          "Built for carnivals, bake sales, bingo, and PAC meetings, not school field trips"
        ]
      },
      "volunteers": {
        "title": "Volunteer sign-up",
        "pitch": "Run slot-based volunteer signup on your school site, with no separate scheduling account for parents.",
        "bullets": [
          "Volunteer slots with date, time, capacity, and sections",
          "Waitlists, coverage views, and rosters for PAC admins",
          "Parent volunteering list on your school subdomain",
          "First-party QR codes and short links into signup flows, with scan counts you control"
        ]
      },
      "communications": {
        "title": "Communications",
        "pitch": "Reach parents on your subdomain without borrowing school admin for email and newsletters.",
        "bullets": [
          "First-party Inbox on yourschool.pachub.ca with optional email of the same message",
          "Draft, publish, and target messages by role. Newsletters are messages plus email.",
          "Evergreen site pages for About, policies, and contact info alongside time-bound updates",
          "Forms and permissions for agreements without sending parents to another form builder",
          "Surveys and community input. Optional SMS reminders when your council wants them."
        ]
      },
      "meetings": {
        "title": "PAC meetings",
        "pitch": "First-party video and hybrid options designed for PAC general and executive sessions.",
        "bullets": [
          "Video rooms for council meetings on your school site, not public Zoom links",
          "Hybrid-friendly with in-person location when your council needs it",
          "Elections and ballots designed for PAC governance workflows",
          "Parents join from the same login they use for lunch and volunteering"
        ]
      },
      "documents": {
        "title": "PAC documents",
        "pitch": "Governance packets and archives that outlive officer turnover.",
        "bullets": [
          "Draft structured agendas into branded PDF packets and attach finals to meeting events",
          "Minutes and governance archives organized by school year",
          "Parents receive meeting documents through your school site",
          "Finalized packets stay with the event, not scattered across inboxes"
        ]
      },
      "schoolYear": {
        "title": "School year, families & members",
        "pitch": "Set up the year, link children to classrooms, and manage roles the way councils actually operate.",
        "bullets": [
          "Enable and archive school years. Copy classrooms forward when a new year starts.",
          "Children and family profiles with classroom picks and allergy or dietary notes",
          "Searchable membership, invites, role edits, and Hot Lunch Coordinator assignment",
          "Ownership handoff when executives change, with year disable for parents",
          "Home dashboard for parents: unread messages, shifts, open lunch windows, and what needs attention"
        ]
      },
      "fundraising": {
        "title": "Fundraising & donations",
        "pitch": "Track campaigns and donations on your PAC site, not a separate portal.",
        "bullets": [
          "Fundraising campaigns with goals and progress tracking",
          "Donation records and tax receipts where your charity status allows",
          "Separate checkout from hot lunch with secure payment processing on your school site",
          "Campaign progress your treasurers can update and share with the board"
        ]
      },
      "budget": {
        "title": "Budget & treasurer tools",
        "pitch": "Budget vs actual reporting and fund separation for treasurers and auditors.",
        "bullets": [
          "Budget vs actual views for monthly and year-to-date reporting",
          "Separate ledgers for general and gaming funds when your council needs them",
          "AGM and auditor-friendly exports integrated with your PAC hub",
          "Licensed gaming checklists and ledgers when BC licence rules apply"
        ]
      },
      "trustPlatform": {
        "title": "Trust & platform",
        "pitch": "Modern security and school-scoped data, with a subscription that funds the platform without selling your community's information.",
        "bullets": [
          "Each school on its own subdomain with role-based access for parents and execs",
          "Modern authentication and school-scoped accounts. Audit log for sensitive admin actions.",
          "We don't sell your community's data to third parties. Your council's subscription funds the platform.",
          "English, French, and Chinese for your school community"
        ]
      }
    },
    "about": {
      "heroTitle": "Built by PACs for PACs",
      "heroDescription": "Parent Advisory Councils run on too many tools and too much borrowed time from a school office already busy with school operations.",
      "problemTitle": "The problem",
      "problemBody": "BC elementary PACs run on a fragmented stack: a hot lunch site, video conferencing, forms and surveys, spreadsheets, custom websites, and short-link tools. Multiple logins, weak continuity when executives turn over, and work that spills onto school office staff who are already focused on running the school.",
      "solutionTitle": "Our approach",
      "solutionBody": "One school site, everything your PAC needs in one place.",
      "solutionPrinciples": "No ads. No selling emails. Your data is safe.",
      "solutionTagline": "One login. One parent community.",
      "founderTitle": "Who's behind it",
      "founderBody": "PAC Hub is built by a two-time PAC chair and software engineer who knows the treasurer spreadsheet, the lunch coordinator's inbox, and the last-minute volunteer plea. Canadian owned and designed for Canadian schools, focused on councils that want one modern hub instead of six bookmarks.",
      "ctaTitle": "Talk with us",
      "ctaDescription": "We're onboarding pilot schools and design partners across BC.",
      "ctaButton": "Contact us"
    },
    "howItWorks": {
      "heroTitle": "Quick setup for your PAC",
      "heroDescription": "PAC Hub gives your council a single place at yourschool.pachub.ca. Here's how families and executives get started.",
      "steps": {
        "schoolSite": {
          "title": "Your council gets a school site",
          "description": "Your PAC is set up on a subdomain of pachub.ca with your school name. Executives manage settings, roles, and billing from the same hub parents use."
        },
        "register": {
          "title": "Parents register with a school code",
          "description": "Parents sign up at yourschool.pachub.ca, verify email, and link children to classrooms. Elevated codes let PAC executives and site admins onboard securely."
        },
        "runYear": {
          "title": "Link children and run the year",
          "description": "Parents add children, pick classrooms, and use one login for lunch, events, messages, and volunteering. Executives publish activities, menus, and updates from the same site."
        },
        "roles": {
          "title": "Roles match real PAC life",
          "description": "Parents, PAC executives, Hot Lunch Coordinators, and site admins each see what they need. Your PAC controls who can access events, messages, and member lists."
        }
      },
      "rolesTitle": "Who uses PAC Hub",
      "roles": {
        "parents": {
          "title": "Parents",
          "description": "One login for lunch orders, volunteer sign-up, messages, and your home dashboard showing what needs attention next."
        },
        "executives": {
          "title": "PAC Executives",
          "description": "Publish events, run lunch programs, send newsletters, manage members, and hand off ownership when roles change."
        },
        "coordinators": {
          "title": "Hot Lunch Coordinators",
          "description": "Manage vendors, menus, cutoffs, and exports without becoming the billing owner or juggling a separate lunch-only account."
        }
      },
      "ctaTitle": "See everything included",
      "ctaButton": "Explore features",
      "pilotCtaTitle": "Join a pilot school",
      "pilotCtaDescription": "Tell us about your council and we'll share how PAC Hub fits your community.",
      "pilotCtaButton": "Get in touch"
    },
    "guides": {
      "heroTitle": "Guides",
      "heroDescription": "Short walkthroughs for signing in and using PAC Hub.",
      "empty": "Walkthroughs will appear here as we publish them.",
      "backToGuides": "All guides",
      "sections": {
        "getting-started": "Getting started"
      },
      "videos": {
        "sign-in": {
          "description": "How to sign in to your school site and open your dashboard."
        },
        "inbox": {
          "description": "How to open Inbox, read a message, and see unread markers update."
        }
      }
    },
    "faq": {
      "heroTitle": "Frequently asked questions",
      "heroDescription": "Answers to common questions about PAC Hub.",
      "items": {
        "whatIsPacHub": {
          "question": "What is PAC Hub?",
          "answer": "PAC Hub is an all-in-one platform for Parent Advisory Councils. Your council gets a site at yourschool.pachub.ca where parents sign in once for hot lunch, events, volunteer sign-up, messages, meetings, and more. One login. One parent community."
        },
        "whoIsItFor": {
          "question": "Who is PAC Hub for?",
          "answer": "Elementary and middle-school PACs, especially in BC, that want one modern hub instead of separate tools for lunch, volunteers, newsletters, and meetings. Parents, executives, treasurers, and Hot Lunch Coordinators each get role-appropriate access."
        },
        "pricing": {
          "question": "How does pricing work?",
          "answer": "Easy to understand pricing with all the tools your PAC needs in one place. Contact us for pilot details and pricing."
        },
        "pilot": {
          "question": "What is a pilot school?",
          "answer": "Pilot schools work with us during early rollout: real PAC workflows, feedback, and a chance to shape the product. Pilots get onboarding support and a direct line to the team. Use the contact form to tell us about your council."
        },
        "hotLunchIncluded": {
          "question": "Is hot lunch included?",
          "answer": "Yes. Hot lunch ordering is core to PAC Hub and included in your subscription, not an upsell. Orders use secure payment processing on your school site."
        },
        "replaceStack": {
          "question": "Does PAC Hub replace our current tools?",
          "answer": "PAC Hub is designed to consolidate lunch ordering, volunteer signup, newsletters, meeting links, forms, and PAC site pages into one login. You won't need separate accounts for each of those workflows on your school subdomain."
        },
        "dataSelling": {
          "question": "Do you sell parent data?",
          "answer": "No. We don't sell parent or PAC contact information to third parties, and we don't run ads against your community."
        },
        "languages": {
          "question": "What languages are supported?",
          "answer": "PAC Hub supports English, French, and Chinese (Simplified) for school communities. More languages coming soon."
        },
        "schoolAdmin": {
          "question": "Does the school office have to run PAC Hub?",
          "answer": "No. PAC Hub is for the council to own its own comms, newsletters, and site content. School staff can have appropriate roles when needed, but the goal is to reduce borrowed time and resources from school office staff who are already focused on running the school."
        },
        "ownership": {
          "question": "What happens when executives change?",
          "answer": "PAC Hub supports ownership transfer, archived school years, and documents that outlive individual officers. New executives inherit a running hub instead of scattered logins and lost files."
        }
      },
      "ctaTitle": "Still have questions?",
      "ctaButton": "Contact us"
    },
    "trust": {
      "secure": "Safe, secure sign-in and school-scoped accounts",
      "noDataSelling": "Your parent community's data isn't sold to third parties",
      "oneFee": "One subscription for your whole PAC. One platform with tools built for Parent Advisory Councils",
      "privacyConsent": "Canadian privacy posture minded for school communities",
      "pacControl": "Your PAC controls who can access events, messages, and member lists",
      "languages": "Multi-language support for your school community"
    },
    "contact": {
      "title": "Contact us",
      "intro": "Interested in becoming a pilot school or want to learn more about PAC Hub?",
      "nameLabel": "Name",
      "namePlaceholder": "Your name",
      "emailLabel": "Email",
      "emailPlaceholder": "Your email",
      "roleLabel": "I am a",
      "rolePlaceholder": "Select your role",
      "roles": {
        "parent": "Parent",
        "pacExecutive": "PAC Executive",
        "schoolStaff": "School Staff",
        "other": "Other"
      },
      "schoolLabel": "School name",
      "schoolPlaceholder": "Your school (optional)",
      "cityLabel": "City or district",
      "cityPlaceholder": "Optional",
      "interestLabel": "I'm interested in",
      "interestPlaceholder": "Select one",
      "interests": {
        "pilotSchool": "Pilot School",
        "demo": "Demo / Walkthrough",
        "general": "General Question"
      },
      "messageLabel": "Message",
      "messagePlaceholder": "Tell us about your PAC and what you're looking for",
      "marketingConsentLabel": "Email me updates about PAC Hub. I can unsubscribe at any time.",
      "privacyDisclosureBefore": "By submitting this form, you agree to our",
      "privacyPolicyLink": "Privacy Policy",
      "submitButton": "Send message",
      "successTitle": "Message sent",
      "successMessage": "Thanks for reaching out. We will get back to you soon.",
      "errorMessage": "We could not send your message. Please try again later."
    },
    "contactValidation": {
      "roleRequired": "Please select your role",
      "interestRequired": "Please select an interest",
      "nameRequired": "Name is required",
      "emailInvalid": "Must be a valid email",
      "messageTooShort": "Message is too short",
      "turnstileRequired": "Please complete the verification"
    },
    "privacy": {
      "title": "Privacy Policy",
      "lastUpdated": "Last updated: June 2026",
      "intro": "This policy describes how PAC Hub (\"we\", \"us\") handles personal information collected through pachub.ca before and during our pilot program.",
      "whoWeAreTitle": "Who we are",
      "whoWeAreBody": "PAC Hub provides software for schools' Parent Advisory Councils. For privacy questions or requests, contact us at <emailLink>{{email}}</emailLink>.",
      "whatWeCollectTitle": "What we collect",
      "whatWeCollectIntro": "When you use our contact form, we may collect:",
      "whatWeCollectItems": {
        "nameEmail": "Name and email address",
        "role": "Your role (for example, parent, PAC executive, school staff)",
        "school": "School name and city or district, if you provide them",
        "message": "Your message and stated interest (for example, pilot school)",
        "marketingOptIn": "Whether you opt in to receive email updates about PAC Hub and pilot opportunities"
      },
      "turnstileBodyBefore": "We use Cloudflare Turnstile to reduce spam. Cloudflare may process technical data as part of that service. See Cloudflare's",
      "turnstileLink": "Turnstile Privacy Addendum",
      "turnstileBodyAfter": "for how they handle that data.",
      "whyWeCollectTitle": "Why we collect it",
      "whyWeCollectItems": {
        "respond": "To respond to your inquiry",
        "followUp": "To follow up about pilot schools or demos you ask about (inquiry-related contact)",
        "marketing": "To send promotional email only if you separately opt in with the marketing checkbox"
      },
      "noSelling": "We do not sell parent or PAC contact information to third parties.",
      "whereStoredTitle": "Where data is stored",
      "whereStoredBody": "When you contact us through this website, we store your submission in secure systems used to operate pachub.ca. Access is limited to authorized PAC Hub team members who need it to respond to inquiries.",
      "retentionTitle": "How long we keep it",
      "retentionBody": "We keep contact records while your inquiry is active and for a reasonable period afterward, or until you ask us to delete them. Marketing opt-in records are kept until you withdraw consent or we no longer need them for the stated purpose.",
      "choicesTitle": "Your choices",
      "choicesItems": {
        "access": "You may request access, correction, or deletion of your information by emailing <emailLink>{{email}}</emailLink>",
        "unsubscribe": "If you opted in to marketing email, you may unsubscribe at any time using the unsubscribe link in those messages or by contacting us"
      },
      "changesTitle": "Changes",
      "changesBody": "We may update this policy as our marketing site and product evolve. The date at the top of this page shows when it was last revised.",
      "contactLink": "Contact us"
    }
  }
}
